import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import { getIngredientPortionTypesForRecipeType, portionTypeQueryCustomFields } from "app/utils/constants/meals";
import { alertMessage } from "../actions/common";
import { AppDispatch } from "../store";
import { errorHandler } from "app/shared/Service/errorHandler";
import {
  getAssignedMealTypesByMealId,
  getCustomMealTypeByIdApi,
  getCustomMealMealTypeSectionIngredientsApi,
  getCustomMealMealTypeSectionsApi,
  postCustomMealMealTypeSectionIngredientApi,
  putCustomMealMealTypeSectionIngredientApi,
  getMealRecipeDetailsApi,
  putMealSyncApi,
  getIngredientByIdApi,
  getIngredientsApi,
  postIngredientApi,
  putIngredientApi,
  getCustomMealTypesApi,
  getCustomMealByIdApi,
  getCustomMealsApi,
  getIngredientClassificationByIdApi,
  getIngredientClassificationsApi,
  postAssignMealTypesToMeals,
  postCustomMealMealTypeSectionApi,
  postCustomMealTypeApi,
  postCustomMealApi,
  postIngredientClassificationApi,
  putAssignMealTypesToMeals,
  putCustomMealMealTypeSectionApi,
  putCustomMealTypeApi,
  putCustomMealApi,
  putIngredientClassificationApi,
} from "app/services/custom-meals";
import { getRawMaterialsByStoreIdApi } from "app/services/allergies";
import { PaginationInterfaceLF } from "app/types";
import {
  CustomMeal,
  CustomMealMealTypeAssignmentHydrated,
  CustomMealMealTypeSectionHydrated,
  CustomMealMealTypeSectionIngredientHydrated,
  GetMealRecipeDetailsApiResponse,
  CustomMealType,
  IngredientClassification,
  UpsertCustomMealPayload,
  UpsertCustomMealMealTypeSectionIngredientPayload,
  UpsertCustomMealMealTypeSectionPayload,
  UpsertCustomMealMealTypePayload,
  UpsertCustomMealTypePayload,
  UpsertIngredientClassificationPayload,
  GetIngredientByIdApiResponse,
  Ingredient,
  UpsertIngredientPayload,
} from "app/types/custom-meals";

interface CustomMealsFilters {
  name?: string;
  portionType?: string;
  recipeType?: string;
}

interface InitialState {
  customMealsList: CustomMeal[];
  customMealsListPagination: PaginationInterfaceLF;
  customMealsListRowsPerPage: number;
  isCustomMealsListLoading: boolean;
  isCustomMealDetailsLoading: boolean;
  isSubmittingCustomMeal: boolean;
  filters: CustomMealsFilters | null;
  subRecipeList: CustomMeal[];
  subRecipeListPagination: PaginationInterfaceLF;
  subRecipeListRowsPerPage: number;
  isSubRecipeListLoading: boolean;
  isSubRecipeDetailsLoading: boolean;
  isSubmittingSubRecipe: boolean;
  subRecipeFilters: CustomMealsFilters | null;
  ingredientsList: Ingredient[];
  ingredientsListPagination: PaginationInterfaceLF;
  ingredientsListRowsPerPage: number;
  isIngredientsListLoading: boolean;
  isIngredientDetailsLoading: boolean;
  isSubmittingIngredient: boolean;
  ingredientsFilters: CustomMealsFilters | null;
  mealRecipeDetailsByKey: Record<string, GetMealRecipeDetailsApiResponse>;
  mealRecipeDetailsLoadingByKey: Record<string, boolean>;
  mealOdooSyncLoadingByMealId: Record<number, boolean>;

  customMealTypesList: CustomMealType[];
  customMealTypesListPagination: PaginationInterfaceLF;
  customMealTypesListRowsPerPage: number;
  isCustomMealTypesListLoading: boolean;
  isCustomMealTypeDetailsLoading: boolean;
  isSubmittingCustomMealType: boolean;
  customMealTypesFilters: CustomMealsFilters | null;

  ingredientClassificationsList: IngredientClassification[];
  ingredientClassificationsListPagination: PaginationInterfaceLF;
  ingredientClassificationsListRowsPerPage: number;
  isIngredientClassificationsListLoading: boolean;
  isIngredientClassificationDetailsLoading: boolean;
  isSubmittingIngredientClassification: boolean;
  ingredientClassificationsFilters: CustomMealsFilters | null;
  customMealAssignedMealTypes: CustomMealMealTypeAssignmentHydrated[];
  isCustomMealAssignedMealTypesLoading: boolean;
  isSubmittingCustomMealAssignedMealType: boolean;
  customMealMealTypeSections: CustomMealMealTypeSectionHydrated[];
  isCustomMealMealTypeSectionsLoading: boolean;
  isSubmittingCustomMealMealTypeSection: boolean;
  mealTypeSectionIngredientsList: CustomMealMealTypeSectionIngredientHydrated[];
  isMealTypeSectionIngredientsListLoading: boolean;
  isSubmittingMealTypeSectionIngredient: boolean;
}

const initialState: InitialState = {
  customMealsList: [],
  customMealsListPagination: null,
  customMealsListRowsPerPage: 50,
  isCustomMealsListLoading: false,
  isCustomMealDetailsLoading: false,
  isSubmittingCustomMeal: false,
  filters: null,
  subRecipeList: [],
  subRecipeListPagination: null,
  subRecipeListRowsPerPage: 50,
  isSubRecipeListLoading: false,
  isSubRecipeDetailsLoading: false,
  isSubmittingSubRecipe: false,
  subRecipeFilters: null,
  ingredientsList: [],
  ingredientsListPagination: null,
  ingredientsListRowsPerPage: 50,
  isIngredientsListLoading: false,
  isIngredientDetailsLoading: false,
  isSubmittingIngredient: false,
  ingredientsFilters: null,
  mealRecipeDetailsByKey: {},
  mealRecipeDetailsLoadingByKey: {},
  mealOdooSyncLoadingByMealId: {},
  customMealTypesList: [],
  customMealTypesListPagination: null,
  customMealTypesListRowsPerPage: 50,
  isCustomMealTypesListLoading: false,
  isCustomMealTypeDetailsLoading: false,
  isSubmittingCustomMealType: false,
  customMealTypesFilters: null,
  ingredientClassificationsList: [],
  ingredientClassificationsListPagination: null,
  ingredientClassificationsListRowsPerPage: 50,
  isIngredientClassificationsListLoading: false,
  isIngredientClassificationDetailsLoading: false,
  isSubmittingIngredientClassification: false,
  ingredientClassificationsFilters: null,
  customMealAssignedMealTypes: [],
  isCustomMealAssignedMealTypesLoading: false,
  isSubmittingCustomMealAssignedMealType: false,
  customMealMealTypeSections: [],
  isCustomMealMealTypeSectionsLoading: false,
  isSubmittingCustomMealMealTypeSection: false,
  mealTypeSectionIngredientsList: [],
  isMealTypeSectionIngredientsListLoading: false,
  isSubmittingMealTypeSectionIngredient: false,
};

export const customMeals = createSlice({
  name: "customMeals",
  initialState,
  reducers: {
    setCustomMealsList: (
      state,
      action: PayloadAction<{
        list: CustomMeal[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.customMealsList = action.payload.list;
      state.customMealsListPagination = action.payload.pagination;
    },
    setCustomMealsListLoading: (state, action: PayloadAction<boolean>) => {
      state.isCustomMealsListLoading = action.payload;
    },
    setCustomMealsListRowsPerPage: (state, action: PayloadAction<number>) => {
      state.customMealsListRowsPerPage = action.payload;
    },
    setCustomMealDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isCustomMealDetailsLoading = action.payload;
    },
    setSubmittingCustomMeal: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingCustomMeal = action.payload;
    },
    setCustomMealsFilters: (
      state,
      action: PayloadAction<CustomMealsFilters | null>,
    ) => {
      state.filters = action.payload;
    },
    setSubRecipeList: (
      state,
      action: PayloadAction<{
        list: CustomMeal[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.subRecipeList = action.payload.list;
      state.subRecipeListPagination = action.payload.pagination;
    },
    setSubRecipeListLoading: (state, action: PayloadAction<boolean>) => {
      state.isSubRecipeListLoading = action.payload;
    },
    setSubRecipeListRowsPerPage: (state, action: PayloadAction<number>) => {
      state.subRecipeListRowsPerPage = action.payload;
    },
    setSubRecipeDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isSubRecipeDetailsLoading = action.payload;
    },
    setSubmittingSubRecipe: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingSubRecipe = action.payload;
    },
    setSubRecipeFilters: (
      state,
      action: PayloadAction<CustomMealsFilters | null>,
    ) => {
      state.subRecipeFilters = action.payload;
    },
    setIngredientsList: (
      state,
      action: PayloadAction<{
        list: Ingredient[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.ingredientsList = action.payload.list;
      state.ingredientsListPagination = action.payload.pagination;
    },
    setIngredientsListLoading: (state, action: PayloadAction<boolean>) => {
      state.isIngredientsListLoading = action.payload;
    },
    setIngredientsListRowsPerPage: (state, action: PayloadAction<number>) => {
      state.ingredientsListRowsPerPage = action.payload;
    },
    setIngredientDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isIngredientDetailsLoading = action.payload;
    },
    setSubmittingIngredient: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingIngredient = action.payload;
    },
    setIngredientsFilters: (
      state,
      action: PayloadAction<CustomMealsFilters | null>,
    ) => {
      state.ingredientsFilters = action.payload;
    },
    setMealRecipeDetailsByKey: (
      state,
      action: PayloadAction<{
        key: string;
        value: GetMealRecipeDetailsApiResponse;
      }>,
    ) => {
      state.mealRecipeDetailsByKey[action.payload.key] = action.payload.value;
    },
    setMealRecipeDetailsLoadingByKey: (
      state,
      action: PayloadAction<{
        key: string;
        value: boolean;
      }>,
    ) => {
      state.mealRecipeDetailsLoadingByKey[action.payload.key] = action.payload.value;
    },
    setMealOdooSyncLoadingByMealId: (
      state,
      action: PayloadAction<{ mealId: number; value: boolean }>,
    ) => {
      if (action.payload.value) {
        state.mealOdooSyncLoadingByMealId[action.payload.mealId] = true;
      } else {
        delete state.mealOdooSyncLoadingByMealId[action.payload.mealId];
      }
    },
    setCustomMealTypesList: (
      state,
      action: PayloadAction<{
        list: CustomMealType[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.customMealTypesList = action.payload.list;
      state.customMealTypesListPagination = action.payload.pagination;
    },
    setCustomMealTypesListLoading: (state, action: PayloadAction<boolean>) => {
      state.isCustomMealTypesListLoading = action.payload;
    },
    setCustomMealTypesListRowsPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.customMealTypesListRowsPerPage = action.payload;
    },
    setCustomMealTypeDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isCustomMealTypeDetailsLoading = action.payload;
    },
    setSubmittingCustomMealType: (state, action: PayloadAction<boolean>) => {
      state.isSubmittingCustomMealType = action.payload;
    },
    setCustomMealTypesFilters: (
      state,
      action: PayloadAction<CustomMealsFilters | null>,
    ) => {
      state.customMealTypesFilters = action.payload;
    },
    setIngredientClassificationsList: (
      state,
      action: PayloadAction<{
        list: IngredientClassification[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.ingredientClassificationsList = action.payload.list;
      state.ingredientClassificationsListPagination = action.payload.pagination;
    },
    setIngredientClassificationsListLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isIngredientClassificationsListLoading = action.payload;
    },
    setIngredientClassificationsListRowsPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.ingredientClassificationsListRowsPerPage = action.payload;
    },
    setIngredientClassificationDetailsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isIngredientClassificationDetailsLoading = action.payload;
    },
    setSubmittingIngredientClassification: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isSubmittingIngredientClassification = action.payload;
    },
    setIngredientClassificationsFilters: (
      state,
      action: PayloadAction<CustomMealsFilters | null>,
    ) => {
      state.ingredientClassificationsFilters = action.payload;
    },
    setCustomMealAssignedMealTypes: (
      state,
      action: PayloadAction<CustomMealMealTypeAssignmentHydrated[]>,
    ) => {
      state.customMealAssignedMealTypes = action.payload;
    },
    setCustomMealAssignedMealTypesLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isCustomMealAssignedMealTypesLoading = action.payload;
    },
    setSubmittingCustomMealAssignedMealType: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isSubmittingCustomMealAssignedMealType = action.payload;
    },
    setCustomMealMealTypeSections: (
      state,
      action: PayloadAction<CustomMealMealTypeSectionHydrated[]>,
    ) => {
      state.customMealMealTypeSections = action.payload;
    },
    setCustomMealMealTypeSectionsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isCustomMealMealTypeSectionsLoading = action.payload;
    },
    setSubmittingCustomMealMealTypeSection: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isSubmittingCustomMealMealTypeSection = action.payload;
    },
    setMealTypeSectionIngredientsList: (
      state,
      action: PayloadAction<CustomMealMealTypeSectionIngredientHydrated[]>,
    ) => {
      state.mealTypeSectionIngredientsList = action.payload;
    },
    clearMealTypeSectionIngredientsList: (state) => {
      state.mealTypeSectionIngredientsList = [];
    },
    setMealTypeSectionIngredientsListLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isMealTypeSectionIngredientsListLoading = action.payload;
    },
    setSubmittingMealTypeSectionIngredient: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isSubmittingMealTypeSectionIngredient = action.payload;
    },
  },
});

export const {
  setCustomMealsList,
  setCustomMealsListLoading,
  setCustomMealsListRowsPerPage,
  setCustomMealDetailsLoading,
  setSubmittingCustomMeal,
  setCustomMealsFilters,
  setSubRecipeList,
  setSubRecipeListLoading,
  setSubRecipeListRowsPerPage,
  setSubRecipeDetailsLoading,
  setSubmittingSubRecipe,
  setSubRecipeFilters,
  setIngredientsList,
  setIngredientsListLoading,
  setIngredientsListRowsPerPage,
  setIngredientDetailsLoading,
  setSubmittingIngredient,
  setIngredientsFilters,
  setMealRecipeDetailsByKey,
  setMealRecipeDetailsLoadingByKey,
  setMealOdooSyncLoadingByMealId,
  setCustomMealTypesList,
  setCustomMealTypesListLoading,
  setCustomMealTypesListRowsPerPage,
  setCustomMealTypeDetailsLoading,
  setSubmittingCustomMealType,
  setCustomMealTypesFilters,
  setIngredientClassificationsList,
  setIngredientClassificationsListLoading,
  setIngredientClassificationsListRowsPerPage,
  setIngredientClassificationDetailsLoading,
  setSubmittingIngredientClassification,
  setIngredientClassificationsFilters,
  setCustomMealAssignedMealTypes,
  setCustomMealAssignedMealTypesLoading,
  setSubmittingCustomMealAssignedMealType,
  setCustomMealMealTypeSections,
  setCustomMealMealTypeSectionsLoading,
  setSubmittingCustomMealMealTypeSection,
  setMealTypeSectionIngredientsList,
  clearMealTypeSectionIngredientsList,
  setMealTypeSectionIngredientsListLoading,
  setSubmittingMealTypeSectionIngredient,
} = customMeals.actions;

export default customMeals.reducer;

export const getCustomMealsListAction = (
  { continutationToken }: { continutationToken?: string | null } = {
    continutationToken: null,
  },
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => {
      customMeals: Pick<InitialState, "filters" | "customMealsListRowsPerPage">;
    },
  ) => {
    try {
      const { storeId } = getActiveConfig();
      const { filters, customMealsListRowsPerPage } = getState().customMeals;
      const query =
        "?" +
        createQuery({
          filter: {
            ...(filters || {}),
            type: "Custom",
            limit: customMealsListRowsPerPage,
            continutationToken,
          },
        });

      dispatch(setCustomMealsListLoading(true));
      const { data } = await getCustomMealsApi(storeId, query);
      dispatch(setCustomMealsListLoading(false));

      dispatch(
        setCustomMealsList({
          list: data.data,
          pagination: data.pagination,
        }),
      );
      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCustomMealsListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const customMealGetByIdAction = (
  storeId: number,
  customMealId: number,
  callback?: (data: CustomMeal | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setCustomMealDetailsLoading(true));
      const { data } = await getCustomMealByIdApi(storeId, customMealId);
      dispatch(setCustomMealDetailsLoading(false));

      if (data?.data) {
        if (callback) callback(data.data);
        return data.data;
      }

      if (callback) callback(null);
      return null;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCustomMealDetailsLoading(false));
      dispatch(alertMessage(message, "error"));
      if (callback) callback(null);
    }
  };
};

type PostCustomMealActionParams = {
  storeId: number;
  payload: UpsertCustomMealPayload;
};

export const postCustomMealAction =
  ({ storeId, payload }: PostCustomMealActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingCustomMeal(true));
      const { data } = await postCustomMealApi({
        storeId,
        payload,
      });
      dispatch(setSubmittingCustomMeal(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getCustomMealsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMeal(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutCustomMealActionParams = {
  storeId: number;
  customMealId: number;
  payload: UpsertCustomMealPayload;
};

export const putCustomMealAction =
  ({ storeId, customMealId, payload }: PutCustomMealActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingCustomMeal(true));
      const { data } = await putCustomMealApi({
        storeId,
        customMealId,
        payload,
      });
      dispatch(setSubmittingCustomMeal(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getCustomMealsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMeal(false));
      dispatch(alertMessage(message, "error"));
    }
  };

export const getSubRecipeListAction = (
  { continutationToken }: { continutationToken?: string | null } = {
    continutationToken: null,
  },
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => {
      customMeals: Pick<
        InitialState,
        "subRecipeFilters" | "subRecipeListRowsPerPage"
      >;
    },
  ) => {
    try {
      const { storeId } = getActiveConfig();
      const { subRecipeFilters, subRecipeListRowsPerPage } = getState().customMeals;
      const query =
        "?" +
        createQuery({
          filter: {
            ...(subRecipeFilters || {}),
            type: "SubRecipe",
            limit: subRecipeListRowsPerPage,
            continutationToken,
          },
        });

      dispatch(setSubRecipeListLoading(true));
      const { data } = await getCustomMealsApi(storeId, query);
      dispatch(setSubRecipeListLoading(false));

      dispatch(
        setSubRecipeList({
          list: data.data,
          pagination: data.pagination,
        }),
      );
      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubRecipeListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const subRecipeGetByIdAction = (
  storeId: number,
  subRecipeId: number,
  callback?: (data: CustomMeal | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubRecipeDetailsLoading(true));
      const { data } = await getCustomMealByIdApi(storeId, subRecipeId);
      dispatch(setSubRecipeDetailsLoading(false));

      if (data?.data) {
        if (callback) callback(data.data);
        return data.data;
      }

      if (callback) callback(null);
      return null;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubRecipeDetailsLoading(false));
      dispatch(alertMessage(message, "error"));
      if (callback) callback(null);
    }
  };
};

type PostSubRecipeActionParams = {
  storeId: number;
  payload: UpsertCustomMealPayload;
};

export const postSubRecipeAction =
  ({ storeId, payload }: PostSubRecipeActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingSubRecipe(true));
      const { data } = await postCustomMealApi({
        storeId,
        payload,
      });
      dispatch(setSubmittingSubRecipe(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getSubRecipeListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingSubRecipe(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutSubRecipeActionParams = {
  storeId: number;
  subRecipeId: number;
  payload: UpsertCustomMealPayload;
};

export const putSubRecipeAction =
  ({ storeId, subRecipeId, payload }: PutSubRecipeActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingSubRecipe(true));
      const { data } = await putCustomMealApi({
        storeId,
        customMealId: subRecipeId,
        payload,
      });
      dispatch(setSubmittingSubRecipe(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getSubRecipeListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingSubRecipe(false));
      dispatch(alertMessage(message, "error"));
    }
  };

export const getIngredientsListAction = (
  { continutationToken }: { continutationToken?: string | null } = {
    continutationToken: null,
  },
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => {
      customMeals: Pick<
        InitialState,
        "ingredientsFilters" | "ingredientsListRowsPerPage"
      >;
    },
  ) => {
    try {
      const { storeId } = getActiveConfig();
      const { ingredientsFilters, ingredientsListRowsPerPage } =
        getState().customMeals;
      const query =
        "?" +
        createQuery({
          filter: {
            ...(ingredientsFilters || {}),
            limit: ingredientsListRowsPerPage,
            continutationToken,
          },
        });

      dispatch(setIngredientsListLoading(true));
      const { data } = await getIngredientsApi(storeId, query);
      dispatch(setIngredientsListLoading(false));

      dispatch(
        setIngredientsList({
          list: data.data,
          pagination: data.pagination,
        }),
      );
      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setIngredientsListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getIngredientByIdAction = (
  ingredientId: number,
  callback?: (data: GetIngredientByIdApiResponse | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIngredientDetailsLoading(true));
      const { data } = await getIngredientByIdApi(storeId, ingredientId);
      dispatch(setIngredientDetailsLoading(false));

      if (data?.data) {
        if (callback) callback(data);
        return data;
      }

      if (callback) callback(null);
      return null;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setIngredientDetailsLoading(false));
      dispatch(alertMessage(message, "error"));
      if (callback) callback(null);
    }
  };
};

type PostIngredientActionParams = {
  payload: UpsertIngredientPayload;
};

export const postIngredientAction =
  ({ payload }: PostIngredientActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingIngredient(true));
      const { data } = await postIngredientApi({
        storeId,
        payload,
      });
      dispatch(setSubmittingIngredient(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getIngredientsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingIngredient(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutIngredientActionParams = {
  ingredientId: number;
  payload: UpsertIngredientPayload;
};

export const putIngredientAction =
  ({ ingredientId, payload }: PutIngredientActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingIngredient(true));
      const { data } = await putIngredientApi({
        storeId,
        ingredientId,
        payload,
      });
      dispatch(setSubmittingIngredient(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getIngredientsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingIngredient(false));
      dispatch(alertMessage(message, "error"));
    }
  };

export const searchRawMaterialsForIngredientReferenceAction =
  ({ search }: { search: string }) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: {
            search: search || "",
            limit: 50,
          },
        });
      const { data } = await getRawMaterialsByStoreIdApi(storeId, query);
      return data.data ?? [];
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      return [];
    }
  };

export const searchSubRecipesForIngredientReferenceAction =
  ({ search }: { search: string }) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: {
            type: "SubRecipe",
            search: search || "",
            limit: 50,
          },
        });
      const { data } = await getCustomMealsApi(storeId, query);
      return data.data ?? [];
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      return [];
    }
  };

export const getCustomMealTypesListAction = (
  {
    continutationToken,
    limit,
  }: { continutationToken?: string | null; limit?: number } = {
    continutationToken: null,
  },
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => {
      customMeals: Pick<
        InitialState,
        "customMealTypesFilters" | "customMealTypesListRowsPerPage"
      >;
    },
  ) => {
    try {
      const { storeId } = getActiveConfig();
      const { customMealTypesFilters, customMealTypesListRowsPerPage } =
        getState().customMeals;
      const query =
        "?" +
        createQuery({
          filter: {
            ...(customMealTypesFilters || {}),
            limit: limit || customMealTypesListRowsPerPage,
            continutationToken,
          },
        });

      dispatch(setCustomMealTypesListLoading(true));
      const { data } = await getCustomMealTypesApi(storeId, query);
      dispatch(setCustomMealTypesListLoading(false));

      dispatch(
        setCustomMealTypesList({
          list: data.data,
          pagination: data.pagination,
        }),
      );

      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCustomMealTypesListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const customMealTypeGetByIdAction = (
  storeId: number,
  mealTypeId: number,
  callback?: (data: CustomMealType | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setCustomMealTypeDetailsLoading(true));
      const { data } = await getCustomMealTypeByIdApi(storeId, mealTypeId);
      dispatch(setCustomMealTypeDetailsLoading(false));

      if (data?.data) {
        if (callback) callback(data.data);
        return data.data;
      }

      if (callback) callback(null);
      return null;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCustomMealTypeDetailsLoading(false));
      dispatch(alertMessage(message, "error"));
      if (callback) callback(null);
    }
  };
};

type PostCustomMealTypeActionParams = {
  storeId: number;
  payload: UpsertCustomMealTypePayload;
};

export const postCustomMealTypeAction =
  ({ storeId, payload }: PostCustomMealTypeActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingCustomMealType(true));
      const { data } = await postCustomMealTypeApi({ storeId, payload });
      dispatch(setSubmittingCustomMealType(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getCustomMealTypesListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMealType(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutCustomMealTypeActionParams = {
  storeId: number;
  mealTypeId: number;
  payload: UpsertCustomMealTypePayload;
};

export const putCustomMealTypeAction =
  ({ storeId, mealTypeId, payload }: PutCustomMealTypeActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingCustomMealType(true));
      const { data } = await putCustomMealTypeApi({
        storeId,
        mealTypeId,
        payload,
      });
      dispatch(setSubmittingCustomMealType(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getCustomMealTypesListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMealType(false));
      dispatch(alertMessage(message, "error"));
    }
  };

export const getIngredientClassificationsListAction = (
  { continutationToken }: { continutationToken?: string | null } = {
    continutationToken: null,
  },
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => {
      customMeals: Pick<
        InitialState,
        "ingredientClassificationsFilters" | "ingredientClassificationsListRowsPerPage"
      >;
    },
  ) => {
    try {
      const { storeId } = getActiveConfig();
      const {
        ingredientClassificationsFilters,
        ingredientClassificationsListRowsPerPage,
      } = getState().customMeals;
      const query =
        "?" +
        createQuery({
          filter: {
            ...(ingredientClassificationsFilters || {}),
            limit: ingredientClassificationsListRowsPerPage,
            continutationToken,
          },
        });

      dispatch(setIngredientClassificationsListLoading(true));
      const { data } = await getIngredientClassificationsApi(storeId, query);
      dispatch(setIngredientClassificationsListLoading(false));

      dispatch(
        setIngredientClassificationsList({
          list: data.data,
          pagination: data.pagination,
        }),
      );

      return data.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setIngredientClassificationsListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const ingredientClassificationGetByIdAction = (
  ingredientClassificationId: number,
  callback?: (data: IngredientClassification | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIngredientClassificationDetailsLoading(true));
      const { data } = await getIngredientClassificationByIdApi(
        storeId,
        ingredientClassificationId,
      );
      dispatch(setIngredientClassificationDetailsLoading(false));

      if (data?.data) {
        if (callback) callback(data.data);
        return data.data;
      }

      if (callback) callback(null);
      return null;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setIngredientClassificationDetailsLoading(false));
      dispatch(alertMessage(message, "error"));
      if (callback) callback(null);
    }
  };
};

type PostIngredientClassificationActionParams = {
  storeId: number;
  payload: UpsertIngredientClassificationPayload;
};

export const postIngredientClassificationAction =
  ({ storeId, payload }: PostIngredientClassificationActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingIngredientClassification(true));
      const { data } = await postIngredientClassificationApi({
        storeId,
        payload,
      });
      dispatch(setSubmittingIngredientClassification(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getIngredientClassificationsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingIngredientClassification(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutIngredientClassificationActionParams = {
  storeId: number;
  ingredientClassificationId: number;
  payload: UpsertIngredientClassificationPayload;
};

export const putIngredientClassificationAction =
  ({
    storeId,
    ingredientClassificationId,
    payload,
  }: PutIngredientClassificationActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmittingIngredientClassification(true));
      const { data } = await putIngredientClassificationApi({
        storeId,
        ingredientClassificationId,
        payload,
      });
      dispatch(setSubmittingIngredientClassification(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getIngredientClassificationsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingIngredientClassification(false));
      dispatch(alertMessage(message, "error"));
    }
  };

export const getCustomMealAssignedMealTypesAction = (customMealId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setCustomMealAssignedMealTypesLoading(true));
      const { data } = await getAssignedMealTypesByMealId({
        storeId,
        customMealId,
      });
      dispatch(setCustomMealAssignedMealTypesLoading(false));

      const mealTypesMap = new Map(
        (data.included?.mealTypes || []).map((mealType) => [mealType.id, mealType]),
      );
      const hydratedAssignments = (data.data || []).map((assignment) => ({
        ...assignment,
        mealType: mealTypesMap.get(assignment.mealTypeId) || null,
      }));

      dispatch(setCustomMealAssignedMealTypes(hydratedAssignments));
      return hydratedAssignments;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCustomMealAssignedMealTypesLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

type PostCustomMealAssignedMealTypeActionParams = {
  customMealId: number;
  payload: UpsertCustomMealMealTypePayload;
};

export const postCustomMealAssignedMealTypeAction =
  ({ customMealId, payload }: PostCustomMealAssignedMealTypeActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingCustomMealAssignedMealType(true));
      const { data } = await postAssignMealTypesToMeals({
        storeId,
        customMealId,
        payload,
      });
      dispatch(setSubmittingCustomMealAssignedMealType(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(getCustomMealAssignedMealTypesAction(customMealId));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMealAssignedMealType(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutCustomMealAssignedMealTypeActionParams = {
  customMealId: number;
  assignmentId: number;
  payload: UpsertCustomMealMealTypePayload;
};

export const putCustomMealAssignedMealTypeAction =
  ({
    customMealId,
    assignmentId,
    payload,
  }: PutCustomMealAssignedMealTypeActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingCustomMealAssignedMealType(true));
      const { data } = await putAssignMealTypesToMeals({
        storeId,
        customMealId,
        assignmentId,
        payload,
      });
      dispatch(setSubmittingCustomMealAssignedMealType(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getCustomMealAssignedMealTypesAction(customMealId));
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMealAssignedMealType(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type GetCustomMealMealTypeSectionsActionParams = {
  customMealId: number;
  customMealTypeId: number;
};

export const getCustomMealMealTypeSectionsAction =
  ({ customMealId, customMealTypeId }: GetCustomMealMealTypeSectionsActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setCustomMealMealTypeSectionsLoading(true));
      const { data } = await getCustomMealMealTypeSectionsApi({
        storeId,
        customMealId,
        customMealTypeId,
      });
      dispatch(setCustomMealMealTypeSectionsLoading(false));

      const ingredientClassificationsMap = new Map(
        (data.included?.ingredientClassifications || []).map((item) => [
          item.id,
          item,
        ]),
      );
      const hydratedSections = (data.data || []).map((section) => ({
        ...section,
        ingredientClassification:
          ingredientClassificationsMap.get(section.ingredientClassificationId) ||
          null,
      }));

      dispatch(setCustomMealMealTypeSections(hydratedSections));
      return hydratedSections;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setCustomMealMealTypeSectionsLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PostCustomMealMealTypeSectionActionParams = {
  customMealId: number;
  customMealTypeId: number;
  payload: UpsertCustomMealMealTypeSectionPayload;
};

export const postCustomMealMealTypeSectionAction =
  ({
    customMealId,
    customMealTypeId,
    payload,
  }: PostCustomMealMealTypeSectionActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingCustomMealMealTypeSection(true));
      const { data } = await postCustomMealMealTypeSectionApi({
        storeId,
        customMealId,
        customMealTypeId,
        payload,
      });
      dispatch(setSubmittingCustomMealMealTypeSection(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(
          getCustomMealMealTypeSectionsAction({ customMealId, customMealTypeId }),
        );
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMealMealTypeSection(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutCustomMealMealTypeSectionActionParams = {
  customMealId: number;
  customMealTypeId: number;
  sectionId: number;
  payload: UpsertCustomMealMealTypeSectionPayload;
};

export const putCustomMealMealTypeSectionAction =
  ({
    customMealId,
    customMealTypeId,
    sectionId,
    payload,
  }: PutCustomMealMealTypeSectionActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingCustomMealMealTypeSection(true));
      const { data } = await putCustomMealMealTypeSectionApi({
        storeId,
        customMealId,
        customMealTypeId,
        sectionId,
        payload,
      });
      dispatch(setSubmittingCustomMealMealTypeSection(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(
          getCustomMealMealTypeSectionsAction({ customMealId, customMealTypeId }),
        );
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingCustomMealMealTypeSection(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type GetCustomMealMealTypeSectionIngredientsActionParams = {
  customMealId: number;
  customMealTypeId: number;
  sectionId: number;
  recipeType: string;
};

export const getCustomMealMealTypeSectionIngredientsAction =
  ({
    customMealId,
    customMealTypeId,
    sectionId,
    recipeType,
  }: GetCustomMealMealTypeSectionIngredientsActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: {
            portionType: getIngredientPortionTypesForRecipeType(recipeType),
          },
          customFields: [...portionTypeQueryCustomFields],
        });
      dispatch(setMealTypeSectionIngredientsListLoading(true));
      const { data } = await getCustomMealMealTypeSectionIngredientsApi({
        storeId,
        customMealId,
        customMealTypeId,
        sectionId,
        query,
      });
      dispatch(setMealTypeSectionIngredientsListLoading(false));

      const ingredientsMap = new Map(
        (data.included?.ingredients || []).map((item) => [item.id, item]),
      );
      const hydrated = (data.data || []).map((row) => ({
        ...row,
        ingredient: ingredientsMap.get(row.ingredientId) ?? null,
      }));

      dispatch(setMealTypeSectionIngredientsList(hydrated));
      return hydrated;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setMealTypeSectionIngredientsListLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PostCustomMealMealTypeSectionIngredientActionParams = {
  customMealId: number;
  customMealTypeId: number;
  sectionId: number;
  recipeType: string;
  payload: UpsertCustomMealMealTypeSectionIngredientPayload;
};

export const postCustomMealMealTypeSectionIngredientAction =
  ({
    customMealId,
    customMealTypeId,
    sectionId,
    recipeType,
    payload,
  }: PostCustomMealMealTypeSectionIngredientActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingMealTypeSectionIngredient(true));
      const { data } = await postCustomMealMealTypeSectionIngredientApi({
        storeId,
        customMealId,
        customMealTypeId,
        sectionId,
        payload,
      });
      dispatch(setSubmittingMealTypeSectionIngredient(false));

      if (data?.data) {
        dispatch(alertMessage("Created successfully", "success"));
        dispatch(
          getCustomMealMealTypeSectionIngredientsAction({
            customMealId,
            customMealTypeId,
            sectionId,
            recipeType,
          }),
        );
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingMealTypeSectionIngredient(false));
      dispatch(alertMessage(message, "error"));
    }
  };

type PutCustomMealMealTypeSectionIngredientActionParams = {
  customMealId: number;
  customMealTypeId: number;
  sectionId: number;
  assignmentId: number;
  recipeType: string;
  payload: UpsertCustomMealMealTypeSectionIngredientPayload;
};

export const putCustomMealMealTypeSectionIngredientAction =
  ({
    customMealId,
    customMealTypeId,
    sectionId,
    assignmentId,
    recipeType,
    payload,
  }: PutCustomMealMealTypeSectionIngredientActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setSubmittingMealTypeSectionIngredient(true));
      const { data } = await putCustomMealMealTypeSectionIngredientApi({
        storeId,
        customMealId,
        customMealTypeId,
        sectionId,
        assignmentId,
        payload,
      });
      dispatch(setSubmittingMealTypeSectionIngredient(false));

      if (data?.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(
          getCustomMealMealTypeSectionIngredientsAction({
            customMealId,
            customMealTypeId,
            sectionId,
            recipeType,
          }),
        );
        return data.data;
      }
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setSubmittingMealTypeSectionIngredient(false));
      dispatch(alertMessage(message, "error"));
    }
  };

export const searchIngredientsForMealTypeSectionPickerAction =
  ({ search, recipeType }: { search: string; recipeType: string }) =>
  async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const query =
        "?" +
        createQuery({
          filter: {
            ...(search.trim() ? { name: search.trim() } : {}),
            portionType: getIngredientPortionTypesForRecipeType(recipeType),
            limit: 50,
          },
          customFields: [...portionTypeQueryCustomFields],
        });
      const { data } = await getIngredientsApi(storeId, query);
      return data.data ?? [];
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
      return [];
    }
  };

type GetMealRecipeDetailsActionParams = {
  mealId: number;
  recipeId: number;
};

export const getMealRecipeDetailsAction =
  ({ mealId, recipeId }: GetMealRecipeDetailsActionParams) =>
  async (dispatch: AppDispatch) => {
    const { storeId } = getActiveConfig();
    const key = `${mealId}:${recipeId}`;
    try {
      dispatch(setMealRecipeDetailsLoadingByKey({ key, value: true }));
      const { data } = await getMealRecipeDetailsApi({ storeId, mealId, recipeId });
      dispatch(setMealRecipeDetailsLoadingByKey({ key, value: false }));
      dispatch(setMealRecipeDetailsByKey({ key, value: data }));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setMealRecipeDetailsLoadingByKey({ key, value: false }));
      dispatch(alertMessage(message, "error"));
    }
  };

type SyncMealWithOdooActionParams = {
  mealId: number;
  recipeId?: number;
};

export const syncMealWithOdooAction =
  ({ mealId, recipeId }: SyncMealWithOdooActionParams) =>
  async (dispatch: AppDispatch) => {
    const { storeId } = getActiveConfig();
    try {
      dispatch(setMealOdooSyncLoadingByMealId({ mealId, value: true }));
      const { data } = await putMealSyncApi({ storeId, mealId });
      dispatch(setMealOdooSyncLoadingByMealId({ mealId, value: false }));
      if (data?.data?.message) {
        dispatch(alertMessage(data.data.message, "success"));
      }
      if (recipeId) {
        await dispatch(getMealRecipeDetailsAction({ mealId, recipeId }));
      }
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setMealOdooSyncLoadingByMealId({ mealId, value: false }));
      dispatch(alertMessage(message, "error"));
    }
  };
